import { useCallback, useEffect, useState } from 'react';

/**
 * useState backed by localStorage, so the value survives unmounting the
 * component (e.g. navigating into a campaign detail and coming back).
 *
 * The stored value is validated before being used: anything unparseable or
 * rejected by `validate` falls back to `defaultValue`, so a stale/corrupt
 * entry can never leave the UI in a broken state.
 */
export function usePersistentState<T>(
  key: string,
  defaultValue: T,
  validate?: (value: unknown) => boolean
): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
  const [value, setValue] = useState<T>(() => read(key, defaultValue, validate));

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full or unavailable (private mode) — persistence is best-effort.
    }
  }, [key, value]);

  const reset = useCallback(() => setValue(defaultValue), [defaultValue]);

  return [value, setValue, reset];
}

function read<T>(key: string, defaultValue: T, validate?: (value: unknown) => boolean): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return defaultValue;
    const parsed = JSON.parse(raw) as unknown;
    if (validate && !validate(parsed)) return defaultValue;
    return parsed as T;
  } catch {
    return defaultValue;
  }
}
